import React, { useState, useMemo } from "react";

const BookingsTable = ({ bookings, webinarNames }) => {
  const [search, setSearch] = useState("");
  const [filterWebinar, setFilterWebinar] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterBerater, setFilterBerater] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterUtmSource, setFilterUtmSource] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const webinarIds = useMemo(() => [...new Set(bookings?.map(b => b?.webinar_id)?.filter(Boolean))], [bookings]);
  const beraters = useMemo(() => [...new Set(bookings?.map(b => b?.berater)?.filter(Boolean))], [bookings]);
  const utmSources = useMemo(() => [...new Set(bookings?.map(b => b?.utm_source)?.filter(Boolean))], [bookings]);

  const filtered = useMemo(() => {
    return bookings?.filter(b => {
      const q = search?.toLowerCase();
      if (q && !`${b?.vorname} ${b?.nachname}`?.toLowerCase()?.includes(q) && !b?.email?.toLowerCase()?.includes(q)) return false;
      if (filterWebinar && b?.webinar_id !== filterWebinar) return false;
      if (filterType && b?.type !== filterType) return false;
      if (filterBerater && b?.berater !== filterBerater) return false;
      if (filterDateFrom && b?.date < filterDateFrom) return false;
      if (filterDateTo && b?.date > filterDateTo) return false;
      if (filterUtmSource && b?.utm_source !== filterUtmSource) return false;
      return true;
    });
  }, [bookings, search, filterWebinar, filterType, filterBerater, filterDateFrom, filterDateTo, filterUtmSource]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "–";
    const p = dateStr?.split("-"); if (p?.length === 3) return `${p[2]}.${p[1]}.${p[0]}`; return dateStr;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "–";
    try { return new Date(ts)?.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ts; }
  };

  const exportCSV = () => {
    const headers = ["Name", "E-Mail", "Telefon", "Datum", "Uhrzeit", "Art", "Berater", "Webinar", "Outlook Sync", "UTM Source", "UTM Medium", "UTM Campaign", "UTM Content", "UTM Term", "Gebucht am"];
    const rows = filtered?.map(b => [
      `${b?.vorname} ${b?.nachname}`,
      b?.email,
      b?.telefon,
      formatDate(b?.date),
      b?.time,
      b?.type === 'onsite' ? 'Vor-Ort' : 'Telefon',
      b?.berater,
      webinarNames?.[b?.webinar_id] || b?.webinar_id,
      b?.outlook_event_id ? 'Synchronisiert' : 'Nicht synchronisiert',
      b?.utm_source || '',
      b?.utm_medium || '',
      b?.utm_campaign || '',
      b?.utm_content || '',
      b?.utm_term || '',
      formatTimestamp(b?.timestamp),
    ]);
    const csv = [headers, ...rows]?.map(r => r?.map(c => `"${c || ''}"`)?.join(","))?.join("\n");
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zba-buchungen-${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const inputStyle = (focused) => ({
    height: '38px',
    padding: '0 12px',
    border: focused ? '1.5px solid #3E4954' : '1.5px solid #dde1e7',
    borderRadius: '10px',
    fontSize: '13px',
    fontFamily: 'var(--font-ui)',
    color: '#1e2b38',
    background: 'white',
    outline: 'none',
    transition: 'border-color 200ms ease, box-shadow 200ms ease',
    boxShadow: focused ? '0 0 0 3px rgba(62, 73, 84, 0.1)' : 'none',
  });

  const utmSourceColors = {
    google: { bg: 'rgba(66, 133, 244, 0.1)', color: '#4285f4' },
    facebook: { bg: 'rgba(24, 119, 242, 0.1)', color: '#1877f2' },
    instagram: { bg: 'rgba(225, 48, 108, 0.1)', color: '#e1306c' },
    webinar: { bg: 'rgba(5, 150, 105, 0.1)', color: '#059669' },
    direct: { bg: 'rgba(107, 122, 141, 0.1)', color: '#6b7a8d' },
    email: { bg: 'rgba(245, 158, 11, 0.1)', color: '#d97706' },
  };

  const getUtmBadgeStyle = (source) => {
    const c = utmSourceColors?.[source?.toLowerCase()] || { bg: 'rgba(62, 73, 84, 0.08)', color: '#3E4954' };
    return { fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px', background: c?.bg, color: c?.color, whiteSpace: 'nowrap' };
  };

  return (
    <div style={{ background: 'white', borderRadius: '20px', border: '1px solid #dde1e7', boxShadow: '0 4px 6px rgba(62,73,84,0.04), 0 10px 20px rgba(62,73,84,0.06)', overflow: 'hidden' }}>
      {/* Header bar */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #dde1e7', background: '#3E4954' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div>
              <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '16px', fontWeight: 700, color: 'white', margin: 0 }}>Alle Buchungen</h2>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', fontFamily: 'var(--font-ui)', margin: 0 }}>{filtered?.length} von {bookings?.length} Einträgen</p>
            </div>
          </div>
          <button
            onClick={exportCSV}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '10px',
              background: 'white', color: '#3E4954',
              border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-ui)',
              transition: 'all 200ms ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            CSV Export
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7a8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Name oder E-Mail suchen…"
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              style={{ ...inputStyle(searchFocused), paddingLeft: '36px', width: '100%' }}
            />
          </div>

          <select
            value={filterWebinar}
            onChange={e => setFilterWebinar(e?.target?.value)}
            style={{ ...inputStyle(false), cursor: 'pointer', minWidth: '160px' }}
          >
            <option value="">Alle Webinare</option>
            {webinarIds?.map(id => (
              <option key={id} value={id}>{webinarNames?.[id] || id}</option>
            ))}
          </select>

          <select
            value={filterType}
            onChange={e => setFilterType(e?.target?.value)}
            style={{ ...inputStyle(false), cursor: 'pointer', minWidth: '130px' }}
          >
            <option value="">Alle Arten</option>
            <option value="onsite">Vor-Ort</option>
            <option value="phone">Telefon</option>
          </select>

          <select
            value={filterBerater}
            onChange={e => setFilterBerater(e?.target?.value)}
            style={{ ...inputStyle(false), cursor: 'pointer', minWidth: '150px' }}
          >
            <option value="">Alle Berater</option>
            {beraters?.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>

          <select
            value={filterUtmSource}
            onChange={e => setFilterUtmSource(e?.target?.value)}
            style={{ ...inputStyle(false), cursor: 'pointer', minWidth: '150px' }}
          >
            <option value="">Alle UTM Sources</option>
            {utmSources?.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <input
            type="date"
            value={filterDateFrom}
            onChange={e => setFilterDateFrom(e?.target?.value)}
            style={{ ...inputStyle(false), minWidth: '140px' }}
            title="Von Datum"
          />
          <input
            type="date"
            value={filterDateTo}
            onChange={e => setFilterDateTo(e?.target?.value)}
            style={{ ...inputStyle(false), minWidth: '140px' }}
            title="Bis Datum"
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)' }}>
          <thead>
            <tr style={{ background: '#f5f6f8' }}>
              {['Name', 'E-Mail', 'Telefon', 'Datum', 'Uhrzeit', 'Art', 'Berater', 'Webinar', 'Outlook Sync', 'UTM Source', 'UTM Medium', 'UTM Campaign', 'UTM Content', 'UTM Term', 'Gebucht am']?.map(col => (
                <th key={col} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7a8d', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered?.length === 0 ? (
              <tr>
                <td colSpan={15} style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7a8d', fontSize: '14px' }}>
                  Keine Buchungen gefunden.
                </td>
              </tr>
            ) : (
              filtered?.map((b, i) => (
                <tr
                  key={b?.id}
                  className="dashboard-table-row"
                  style={{ borderBottom: i < filtered?.length - 1 ? '1px solid #dde1e7' : 'none', transition: 'background 150ms ease' }}
                >
                  <td style={{ padding: '13px 16px', fontSize: '14px', fontWeight: 600, color: '#1e2b38', whiteSpace: 'nowrap' }}>
                    {b?.vorname} {b?.nachname}
                  </td>
                  <td style={{ padding: '13px 16px', fontSize: '13px', color: '#6b7a8d' }}>{b?.email}</td>
                  <td style={{ padding: '13px 16px', fontSize: '13px', color: '#6b7a8d', whiteSpace: 'nowrap' }}>{b?.telefon}</td>
                  <td style={{ padding: '13px 16px', fontSize: '13px', color: '#1e2b38', whiteSpace: 'nowrap' }}>{formatDate(b?.date)}</td>
                  <td style={{ padding: '13px 16px', fontSize: '13px', color: '#1e2b38', whiteSpace: 'nowrap' }}>{b?.time} Uhr</td>
                  <td style={{ padding: '13px 16px' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px',
                      background: b?.type === 'onsite' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                      color: b?.type === 'onsite' ? '#059669' : '#3b82f6',
                      whiteSpace: 'nowrap',
                    }}>
                      {b?.type === 'onsite' ? 'Vor-Ort' : 'Telefon'}
                    </span>
                  </td>
                  <td style={{ padding: '13px 16px', fontSize: '13px', color: '#1e2b38', whiteSpace: 'nowrap' }}>{b?.berater}</td>
                  <td style={{ padding: '13px 16px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px', background: 'rgba(62, 73, 84, 0.08)', color: '#3E4954', whiteSpace: 'nowrap' }}>
                      {webinarNames?.[b?.webinar_id] || b?.webinar_id}
                    </span>
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    {b?.outlook_event_id ? (
                      <a
                        href={b?.outlook_web_link || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px', background: 'rgba(5, 150, 105, 0.12)', color: '#059669', whiteSpace: 'nowrap', cursor: 'pointer', display: 'inline-block' }}>
                          ✓ In Outlook
                        </span>
                      </a>
                    ) : (
                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '100px', background: 'rgba(107, 122, 141, 0.1)', color: '#9aa3ad', whiteSpace: 'nowrap', display: 'inline-block' }}>
                        Nicht synchronisiert
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '13px 16px', fontSize: '12px', color: '#6b7a8d', whiteSpace: 'nowrap' }}>{b?.utm_medium || '–'}</td>
                  <td style={{ padding: '13px 16px', fontSize: '12px', color: '#6b7a8d', whiteSpace: 'nowrap', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b?.utm_campaign || '–'}</td>
                  <td style={{ padding: '13px 16px', fontSize: '12px', color: '#6b7a8d', whiteSpace: 'nowrap' }}>{b?.utm_content || '–'}</td>
                  <td style={{ padding: '13px 16px', fontSize: '12px', color: '#6b7a8d', whiteSpace: 'nowrap' }}>{b?.utm_term || '–'}</td>
                  <td style={{ padding: '13px 16px', fontSize: '12px', color: '#6b7a8d', whiteSpace: 'nowrap' }}>{formatTimestamp(b?.timestamp)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BookingsTable;
